import { Link } from "react-router-dom";
import { Phone, ArrowRight, Eye, Glasses, Zap, Droplet, Sparkles, Star, ExternalLink } from "lucide-react";
import { CONTACT, IMAGES, SERVICES, BRANDS, REVIEWS, STORY, GOOGLE_REVIEWS_URL } from "../data/site";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import { CallToAction } from "../components/Sections";

const iconMap = { Eye, Glasses, Zap, Droplet, Sparkles };
const STAR_KEYS = ["star-1", "star-2", "star-3", "star-4", "star-5"];
const CARD_ICON_SIZE = 26;
const STAR_ICON_SIZE = 15;

const Hero = () => (
  <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-navy">
    <img
      src={IMAGES.heroExam}
      alt="Optician helping a patient"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/20" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24">
      <Reveal>
        <span className="inline-flex items-center gap-2 text-tiffany border border-tiffany/40 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] mb-7">
          <span className="w-1.5 h-1.5 rounded-full bg-tiffany animate-pulse" />
          Now Accepting New Patients
        </span>
      </Reveal>
      <Reveal delay={0.1}>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-white max-w-3xl leading-[1.05]">
          Expert Eye Care &<br />Designer Eyewear
        </h1>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="text-white/70 text-lg mt-7 max-w-xl leading-relaxed">
          Comprehensive eye exams, an on-site lab for glasses in about an
          hour, TempSure dry eye treatment, and TempSure skin treatments —
          all under one roof in Brodheadsville, PA.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="flex flex-wrap items-center gap-4 mt-10">
          <a
            href={CONTACT.phoneHref}
            data-testid="hero-call-button"
            className="inline-flex items-center gap-3 bg-tiffany hover:bg-tiffany-dark text-white px-7 py-4 rounded-sm text-base font-semibold transition-colors active:scale-[0.98]"
          >
            <Phone size={18} /> Call {CONTACT.phone}
          </a>
          <Link
            to="/services"
            data-testid="hero-services-link"
            className="inline-flex items-center gap-2 text-white border border-white/30 hover:border-tiffany hover:text-tiffany px-7 py-4 rounded-sm text-base font-semibold transition-colors"
          >
            Our Services <ArrowRight size={18} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const ServiceCard = ({ service, index }) => {
  const Icon = iconMap[service.icon];
  return (
    <Reveal
      delay={index * 0.06}
      className={index === 0 ? "md:col-span-2 md:row-span-1" : ""}
    >
      <Link
        to="/services"
        data-testid={`service-card-${service.id}`}
        className="group relative block h-full overflow-hidden rounded-sm border border-border"
      >
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-7">
          <Icon className="text-tiffany mb-3" size={CARD_ICON_SIZE} />
          <h3 className="font-serif text-2xl font-semibold text-white">
            {service.title}
          </h3>
          <p className="text-white/70 text-sm mt-2 max-w-md">{service.short}</p>
        </div>
      </Link>
    </Reveal>
  );
};

const ServicesPreview = () => (
  <section data-testid="services-preview" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <div>
            <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
              What We Offer
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy max-w-xl">
              Complete care for your eyes & beyond
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-navy font-semibold hover:text-tiffany transition-colors"
            data-testid="services-view-all"
          >
            View all services <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
      <div className="grid gap-5 md:grid-cols-3 auto-rows-[260px]">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.id} service={s} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const StoryTeaser = () => (
  <section data-testid="story-teaser" className="bg-muted py-24 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12 items-center">
      <Reveal className="lg:col-span-2">
        <div className="relative aspect-[4/5] rounded-sm overflow-hidden max-w-sm mx-auto lg:mx-0">
          <img
            src={STORY.signature.photo}
            alt={STORY.signature.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>
      </Reveal>
      <Reveal delay={0.12} className="lg:col-span-3">
        <div>
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            {STORY.eyebrow}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy mb-6">
            {STORY.title}
          </h2>
          <p className="text-muted-foreground text-base leading-relaxed mb-4">
            {STORY.part1[0]}
          </p>
          <p className="font-serif text-xl text-navy italic mb-8">
            "{STORY.highlight}"
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
            <Link
              to="/about"
              data-testid="read-story-link"
              className="inline-flex items-center gap-3 bg-navy hover:bg-navy/90 text-white px-7 py-4 rounded-sm font-semibold transition-colors active:scale-[0.98]"
            >
              Read Our Story <ArrowRight size={18} />
            </Link>
            <div>
              <p className="font-serif text-lg font-semibold text-navy leading-tight">— {STORY.signature.name}</p>
              <p className="text-tiffany text-sm font-medium">{STORY.signature.role}</p>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ReviewCard = ({ review, index }) => (
  <Reveal delay={index * 0.08}>
    <div
      data-testid={`review-card-${index}`}
      className="h-full bg-white border border-border rounded-sm p-7 hover:shadow-sm transition-shadow flex flex-col"
    >
      <div className="flex gap-0.5 mb-4">
        {STAR_KEYS.map((k) => (
          <Star key={k} size={STAR_ICON_SIZE} className="fill-tiffany text-tiffany" />
        ))}
      </div>
      <p className="text-foreground text-sm leading-relaxed mb-5 flex-1">"{review.text}"</p>
      <p className="font-semibold text-navy text-sm">— {review.source}</p>
    </div>
  </Reveal>
);

const ReviewsSection = () => (
  <section data-testid="reviews-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="text-center mb-14">
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            Patient Reviews
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy">
            What our patients say
          </h2>
        </div>
      </Reveal>
      <div className="grid gap-6 md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <ReviewCard key={r.text.slice(0, 40)} review={r} index={i} />
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="text-center mt-12">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-7 py-3.5 rounded-sm font-semibold transition-colors"
          >
            See all reviews on Google <ExternalLink size={16} />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const InsuranceBanner = () => (
  <section data-testid="insurance-banner" className="bg-navy py-16">
    <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <Reveal>
        <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
          Insurance
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-white">
          We accept most vision &amp; medical insurance plans
        </h2>
        <p className="text-white/60 text-sm mt-4">
          Not sure if we take yours? Give us a call and we'll be happy to check for you.
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={BRANDS} label="brands" />
      <ServicesPreview />
      <StoryTeaser />
      <ReviewsSection />
      <InsuranceBanner />
      <CallToAction />
    </div>
  );
}
